import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { AiOutlineFileImage, AiOutlineFilePdf } from "react-icons/ai";
import { LiaFileUploadSolid } from "react-icons/lia";
import { useParams } from "react-router-dom";
import {
 ActionIcon,  Alert,
  Button,
  ComboboxItem,
  Group,
  Loader,
  Modal,
  NumberInput,
  ScrollAreaAutosize,
  Select,
  Stack,
  Tabs,
  Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconCalendar, IconInfoCircle, IconTrash } from "@tabler/icons-react";

import { StyledEditModalContent } from "@pages/Patients/Laboratory/components/EditModal/EditModal.styled";

import { truncateWithEllipsis } from "@utils/truncateText";

import { documentFileTypes, imageFileTypes } from "@constants/fileTypes";
import { MAX_FILENAME_LENGTH_BEFORE_UPLOAD, MEGABYTE } from "@constants/index";

import { useCreateTreatmentPlan } from "@hooks/mutations/consultation/useCreateTreatmentPlan";
import { useUpdateTreatmentPlan } from "@hooks/mutations/consultation/useUpdateTreatmentPlan";
import { useGetClinicTreatmentPlans } from "@hooks/queries/clinic/useGetClinicTreatmentPlans";

import { useAuth } from "@contexts/auth";


interface ModalProps {
  opened: boolean;
  close: () => void;
  supportStaffData: ComboboxItem[] | [];
}

export const AddTreatmentPlanModal = ({
  opened,
  close,
  supportStaffData,
}: ModalProps) => {
  const { t } = useTranslation();
  const { consultationId, patientId } = useParams();
  const { token } = useAuth();

  const [date, setDate] = useState<Date | null>(null);
  const [treatment, setTreatment] = useState<ComboboxItem | null>(null);
  const [quantity, setQuantity] = useState<string | number>(1);
  const [supportStaff, setSupportStaff] = useState<ComboboxItem | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[] | []>([]);

  const {
    data: clinicTreatmentPlans,
    isLoading: isClinicTreatmentPlansLoading,
  } = useGetClinicTreatmentPlans();
  const {
    mutate: createTreatmentPlan,
    isLoading: isCreateTreatmentPlanLoading,
  } = useCreateTreatmentPlan();
  const {
    mutate: updateTreatmentPlan,
    isLoading: isUpdateTreatmentPlanLoading,
  } = useUpdateTreatmentPlan();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([...uploadedFiles, ...acceptedFiles]);
    },
    [uploadedFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "image/*": imageFileTypes,
      "application/pdf": documentFileTypes,
    },
    onDrop,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };

  const handleCreateTreatmentPlan = () => {
    if (!date || !treatment || !quantity || !supportStaff) return;

    const jsFormData = new FormData();

    uploadedFiles?.length &&
      uploadedFiles.forEach(file => {
        jsFormData.append("files", file);
      });

    jsFormData.append("consultation_id", consultationId ?? "");
    jsFormData.append("patient_id", patientId ?? "");
    jsFormData.append("quantity", quantity.toString());
    jsFormData.append("date", date.toISOString());
    jsFormData.append("aid", supportStaff.value ?? "");
    jsFormData.append("service", treatment?.value ?? "");

    console.group("jsFormData");
    jsFormData.forEach((value, key) => {
      console.log(`${key}: ${value}`);
    });
    console.groupEnd();

    createTreatmentPlan(jsFormData, {
      onSuccess: () => {
        close();
        setDate(null);
        setTreatment(null);
        setQuantity(1);
        setSupportStaff(null);
        setUploadedFiles([]);
      },
    });
  };

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("create-treatment-plan")}</Text>}
      opened={opened}
      onClose={close}
      scrollAreaComponent={ScrollAreaAutosize}>
      <StyledEditModalContent>
        <Tabs defaultValue="info">
          <Tabs.List grow>
            <Tabs.Tab value="info">{t("info")}</Tabs.Tab>
            <Tabs.Tab value="add-files">{t("add-files")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="info">
            <form className="form">
              <Stack>
                <Select
                  withAsterisk
                  data={clinicTreatmentPlans ?? []}
                  label={t("treatment")}
                  rightSection={
                    isClinicTreatmentPlansLoading ? (
                      <Loader size={16} color="accent" />
                    ) : null
                  }
                  placeholder={t("select-treatment")}
                  searchable
                  value={treatment?.value ?? null}
                  onChange={(_, option) => setTreatment(option)}
                />
                <NumberInput
                  label={t("quantity")}
                  min={1}
                  withAsterisk
                  defaultValue={1}
                  value={quantity}
                  onChange={setQuantity}
                />
                <Select
                  label={t("support-staff")}
                  data={supportStaffData}
                  placeholder={t("select-staff")}
                  withAsterisk
                  searchable
                  clearable
                  value={supportStaff?.value ?? null}
                  onChange={(_, option) => setSupportStaff(option)}
                />
                <DatePickerInput
                  withAsterisk
                  label={t("date")}
                  // minDate={new Date()}
                  leftSection={<IconCalendar size={16} stroke={1.5} />}
                  value={date}
                  onChange={setDate}
                />
              </Stack>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="add-files">
            <div className="files-wrapper">
              <div
                {...getRootProps({
                  className: `dropzone ${isDragActive ? "active" : ""} ${
                    isDragAccept ? "accept" : ""
                  } ${isDragReject ? "reject" : ""}`,
                })}>
                <input {...getInputProps()} required name="dropzone" />

                <div className="text-content">
                  <div className="svg-wrapper">
                    <LiaFileUploadSolid />
                  </div>
                  <div className="p-wrapper">
                    <p>{t("drag-file-here")}</p>
                    <p>{t("drag-file-here-continuation")}</p>
                  </div>
                </div>
              </div>
              <div className="uploaded-files">
                <h4>{t("uploaded-files")}</h4>
                {!uploadedFiles?.length ? (
                  <Alert
                    mt={"md"}
                    variant="light"
                    color="blue"
                    icon={<IconInfoCircle />}>
                    <Text fz={14} lh={1.5} c="blue">
                      {t("files-appear-info")}
                    </Text>
                  </Alert>
                ) : (
                  <ScrollAreaAutosize h={200} type="auto" mt="sm">
                    <ul>
                      {uploadedFiles.map(file => (
                        <li key={file.name}>
                          <div className="content">
                            {file.type === "application/pdf" ? (
                              <AiOutlineFilePdf />
                            ) : (
                              <AiOutlineFileImage />
                            )}
                            <p>
                              {truncateWithEllipsis(
                                file.name,
                                MAX_FILENAME_LENGTH_BEFORE_UPLOAD
                              )}{" "}
                              ({(file.size / MEGABYTE).toFixed(2)} {t("mb")})
                            </p>
                          </div>

                          <ActionIcon
                            variant="outline"
                            color="red"
                            onClick={() => removeFile(file)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </li>
                      ))}
                    </ul>
                  </ScrollAreaAutosize>
                )}
              </div>
            </div>
          </Tabs.Panel>
        </Tabs>

        <Group justify="end" mt={"xl"} pb={"md"}>
          <PermissionGate permission="treatments.create"><Button
            loading={
              isCreateTreatmentPlanLoading || isUpdateTreatmentPlanLoading
            }
            disabled={!date || !treatment || !quantity || !supportStaff}
            onClick={handleCreateTreatmentPlan}>
            {t("create-treatment-plan")}
          </Button></PermissionGate>
        </Group>
      </StyledEditModalContent>
    </Modal>
  );
};
